import React, { useEffect, useState, useMemo } from "react";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import CarCard from "../components/CarCard";
import SeoHead from "../components/SeoHead";
import Testimonials from "../components/Testimonials";
import Faq from "../components/Faq";
import QuoteForm from "../components/QuoteForm";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useSettings } from "../contexts/SettingsContext";

const HERO_IMAGE = "https://images.unsplash.com/photo-1592309905620-e5b59f6dcb98?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  const { t, lang } = useI18n();
  const { settings } = useSettings();
  const [cars, setCars] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ brand: "", transmission: "", min_price: "", max_price: "" });

  useEffect(() => {
    api.get("/cars/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
    api.get("/cars", { params }).then((r) => setCars(r.data)).finally(() => setLoading(false));
  }, [filters]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const reset = () => setFilters({ brand: "", transmission: "", min_price: "", max_price: "" });
  const isFiltered = filters.brand !== "" || filters.transmission !== "" || filters.min_price !== "" || filters.max_price !== "";

  const brandName = settings?.brand_name || "Inchirieri Auto Pascani";
  const tagline = lang === "ro" ? settings?.tagline_ro : settings?.tagline_en;
  const hero = settings?.hero || {};
  const heroImg = hero.image_path ? (hero.image_path.startsWith("http") ? hero.image_path : `${process.env.REACT_APP_BACKEND_URL}${hero.image_path}`) : HERO_IMAGE;
  const heroEyebrow = (lang === "en" ? hero.eyebrow_en : hero.eyebrow_ro) || "Pascani · Romania";
  const heroTitle = (lang === "en" ? hero.title_en : hero.title_ro) || t("hero.title");
  const heroSubtitle = (lang === "en" ? hero.subtitle_en : hero.subtitle_ro) || t("hero.subtitle");
  const heroCtaLabel = (lang === "en" ? hero.cta_label_en : hero.cta_label_ro) || t("hero.cta");
  const heroCtaHref = hero.cta_href || "#fleet";

  const seo = settings?.seo || {};
  const seoTitle = (lang === "en" ? seo.title_en : seo.title_ro) || `${brandName} — ${tagline || ""}`;
  const seoDescription = (lang === "en" ? seo.description_en : seo.description_ro) || "";
  const ogImage = seo.og_image ? (seo.og_image.startsWith("http") ? seo.og_image : `${process.env.REACT_APP_BACKEND_URL}${seo.og_image}`) : heroImg;

  const businessType = seo.business_type || "AutoRental";
  const openingHoursSpec = (seo.opening_hours || [])
    .filter((oh) => oh && oh.days?.length && oh.open && oh.close)
    .map((oh) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: oh.days.map((d) => ({ Mo: "Monday", Tu: "Tuesday", We: "Wednesday", Th: "Thursday", Fr: "Friday", Sa: "Saturday", Su: "Sunday" }[d] || d)),
      opens: oh.open, closes: oh.close,
    }));
  const sameAs = [seo.social_facebook, seo.social_instagram, seo.social_youtube, seo.social_tiktok, seo.social_linkedin, seo.social_twitter].filter(Boolean);
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": businessType,
    name: brandName,
    description: (lang === "en" ? seo.business_description_en : seo.business_description_ro) || tagline,
    address: { "@type": "PostalAddress", streetAddress: seo.address_street || settings?.contact?.address || "", addressLocality: seo.address_locality || "Pascani", addressRegion: seo.address_region || "Iasi", postalCode: seo.address_postal_code || "", addressCountry: seo.address_country || "RO" },
    telephone: settings?.contact?.phone || "",
    email: settings?.contact?.email || "",
    image: ogImage, url: siteUrl, priceRange: seo.price_range || "€€",
    ...(seo.founding_date ? { foundingDate: seo.founding_date } : {}),
    ...(seo.geo_latitude && seo.geo_longitude ? { geo: { "@type": "GeoCoordinates", latitude: Number(seo.geo_latitude), longitude: Number(seo.geo_longitude) } } : {}),
    ...(openingHoursSpec.length > 0 ? { openingHoursSpecification: openingHoursSpec } : {}),
    ...(seo.service_area_ro || seo.service_area_en ? { areaServed: ((lang === "en" ? seo.service_area_en : seo.service_area_ro) || "").split(",").map((s) => s.trim()).filter(Boolean) } : {}),
    ...(sameAs.length > 0 ? { sameAs } : {}),
  };

  return (
    <div>
      <SeoHead title={seoTitle} description={seoDescription} image={ogImage} jsonLd={jsonLd} jsonLdId="ld-home" />
      <PublicHeader />

      {/* ─── HERO ─── */}
      <section
        className="relative h-screen flex flex-col justify-end hero-grain overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background image — shifted right so left text is readable */}
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt={heroTitle}
            className="w-full h-full object-cover"
            style={{ objectPosition: "60% center", transform: "scale(1.04)", transformOrigin: "right center" }}
          />
          {/* Dual gradient: left dark for text, bottom dark for footer overlap */}
          <div
            className="absolute inset-0"
            style={{
              background: [
                "linear-gradient(100deg, rgba(5,5,5,0.97) 0%, rgba(5,5,5,0.88) 32%, rgba(5,5,5,0.55) 58%, rgba(5,5,5,0.18) 100%)",
                "linear-gradient(0deg, rgba(5,5,5,0.98) 0%, rgba(5,5,5,0.5) 28%, transparent 55%)",
              ].join(", "),
            }}
          />
        </div>

        {/* Decorative vertical brand text — right side */}
        <div
          className="absolute right-6 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-4 z-10"
          aria-hidden="true"
        >
          <div style={{ width: 1, height: 56, background: "rgba(212,175,55,0.25)" }} />
          <span className="hero-vertical-text">{brandName}</span>
          <div style={{ width: 1, height: 56, background: "rgba(212,175,55,0.25)" }} />
        </div>

        {/* Main content — anchored bottom-left */}
        <div className="relative z-10 max-w-[1440px] w-full mx-auto px-6 md:px-12 pb-20 md:pb-28">
          <div
            className="label-eyebrow mb-5 animate-fade-up delay-100 flex items-center gap-3"
            style={{ color: "var(--brand-primary)" }}
          >
            <span style={{ display: "inline-block", width: 28, height: 1, background: "var(--brand-primary)", verticalAlign: "middle" }} />
            {heroEyebrow}
          </div>

          <h1
            className="font-serif font-light animate-fade-up delay-200"
            style={{
              fontSize: "clamp(2.8rem, 7.5vw, 8.5rem)",
              lineHeight: 0.93,
              letterSpacing: "-0.03em",
              maxWidth: "13ch",
              marginBottom: "clamp(2rem, 4vw, 3.5rem)",
            }}
          >
            {heroTitle}
          </h1>

          <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-12 animate-fade-up delay-300">
            <p
              className="text-sm leading-relaxed max-w-[32ch]"
              style={{ color: "var(--brand-text-secondary)" }}
            >
              {heroSubtitle}
            </p>
            <a
              href={heroCtaHref}
              className="btn-gold shrink-0"
              data-testid="hero-cta"
            >
              {heroCtaLabel}
            </a>
          </div>
        </div>

        {/* Animated scroll line */}
        <div className="absolute bottom-9 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
          <div className="scroll-line" />
        </div>
      </section>

      {/* ─── FLEET ─── */}
      <section id="fleet" className="py-24 md:py-36 px-6 md:px-12 max-w-[1440px] mx-auto" data-testid="fleet-section">

        {/* Section header */}
        <div className="mb-14">
          <div className="label-eyebrow mb-4" style={{ color: "var(--brand-primary)" }}>
            {t("home.filtersTitle")}
          </div>
          <h2
            className="font-serif font-light tracking-tight"
            style={{ fontSize: "clamp(2.2rem, 4.5vw, 4rem)" }}
          >
            {t("home.fleet")}
          </h2>
        </div>

        {/* Filters — pill-based */}
        <div
          className="flex flex-wrap gap-2 items-center pb-8 mb-12 border-b"
          style={{ borderColor: "var(--brand-border)" }}
        >
          {/* Brand pills */}
          <div className="flex flex-wrap gap-2" data-testid="filter-brand">
            <button
              onClick={() => setFilter("brand", "")}
              className={`filter-pill${filters.brand === "" ? " active" : ""}`}
            >
              {t("home.allBrands")}
            </button>
            {brands.map((b) => (
              <button
                key={b}
                onClick={() => setFilter("brand", filters.brand === b ? "" : b)}
                className={`filter-pill${filters.brand === b ? " active" : ""}`}
              >
                {b}
              </button>
            ))}
          </div>

          {/* Vertical divider */}
          <div
            className="hidden sm:block self-stretch"
            style={{ width: 1, background: "var(--brand-border)", margin: "0 0.25rem" }}
          />

          {/* Transmission pills */}
          <div className="flex gap-2" data-testid="filter-transmission">
            <button
              onClick={() => setFilter("transmission", "")}
              className={`filter-pill${filters.transmission === "" ? " active" : ""}`}
            >
              {t("home.allTransmissions")}
            </button>
            <button
              onClick={() => setFilter("transmission", "automatic")}
              className={`filter-pill${filters.transmission === "automatic" ? " active" : ""}`}
            >
              {t("common.automatic")}
            </button>
            <button
              onClick={() => setFilter("transmission", "manual")}
              className={`filter-pill${filters.transmission === "manual" ? " active" : ""}`}
            >
              {t("common.manual")}
            </button>
          </div>

          {/* Price range — minimal inline inputs */}
          <div className="flex items-center gap-2 sm:ml-auto">
            <input
              type="number"
              placeholder="Min €"
              value={filters.min_price}
              onChange={(e) => setFilter("min_price", e.target.value)}
              className="w-16 bg-transparent outline-none text-center py-1 border-b"
              style={{ borderColor: "var(--brand-border)", color: "var(--brand-text)", fontSize: "0.72rem", letterSpacing: "0.05em" }}
              data-testid="filter-min-price"
            />
            <span style={{ color: "var(--brand-text-secondary)", fontSize: "0.75rem" }}>—</span>
            <input
              type="number"
              placeholder="Max €"
              value={filters.max_price}
              onChange={(e) => setFilter("max_price", e.target.value)}
              className="w-16 bg-transparent outline-none text-center py-1 border-b"
              style={{ borderColor: "var(--brand-border)", color: "var(--brand-text)", fontSize: "0.72rem", letterSpacing: "0.05em" }}
              data-testid="filter-max-price"
            />
            {isFiltered && (
              <button
                onClick={reset}
                className="label-eyebrow hover:text-[var(--brand-primary)] transition-colors ml-1"
                style={{ color: "var(--brand-text-secondary)" }}
                data-testid="filter-reset"
              >
                × reset
              </button>
            )}
          </div>
        </div>

        {/* Cars grid */}
        {loading ? (
          <div className="text-center py-24 text-sm" style={{ color: "var(--brand-text-secondary)" }}>
            {t("common.loading")}
          </div>
        ) : cars.length === 0 ? (
          <div className="text-center py-24 text-sm" style={{ color: "var(--brand-text-secondary)" }} data-testid="no-results">
            {t("common.noResults")}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16" data-testid="cars-grid">
            {cars.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
          </div>
        )}
      </section>

      <Testimonials />
      <QuoteForm />
      <Faq />
      <PublicFooter />
    </div>
  );
}
